
/**
 * Dark Attribute Card Triggers (闇属性トリガー)
 *
 * This file contains trigger implementations for dark attribute cards.
 * Dark attribute focuses on:
 * - Mill effects (deck destruction)
 * - Graveyard manipulation (banish effects)
 * - Control effects (stealing monsters, hand disruption)
 * - Debuff effects (attack reduction, status removal)
 * - Search effects (theme support)
 *
 * Total: 45 cards
 * - 召喚時 (On Summon): 17 cards
 * - 自壊時 (On Self-Destroy): 10 cards
 * - 攻撃時 (On Attack): 4 cards
 * - 常時 (Continuous): 7 cards
 * - メインフェイズ時 (Main Phase): 2 cards
 * - エンドフェイズ時 (End Phase): 4 cards
 * - バトルフェイズ開始時 (Battle Phase Start): 1 card
 * - 相手モンスター攻撃時 (Opponent Attack): 1 card
 * - フェイズカード (Phase Card): 3 cards
 * - フィールドカード (Field Card): 5 cards
 * - 特殊能力 (Special Abilities): 2 cards (死触)
 */

import { TRIGGER_TYPES, ACTIVATION_TYPES } from '../triggerTypes';
import type { TriggerContext } from '../triggerEngine';
import { unregisterCardTriggers } from '../triggerEngine';
import type { CardWithId, MonsterInstance } from '../../types/card';
import type { TriggerDefinition } from '../../types/effect';
import { STATUS_EFFECT_TYPES, statusEffectEngine } from '../statusEffects';
import {
  getPlayerContext,
  millDeck,
  millOpponentDeck,
  conditionalDamage,
  searchCard,
  drawCards,
  healLife,
  destroyMonster,
  modifyAttack,
  selectAndApplyStatusToOpponent,
} from '../effectHelpers';

/**
 * Dark Card Triggers Registry
 * Key: Card ID (e.g., 'C0000094')
 * Value: Array of trigger objects
 */
export const darkCardTriggers = {
  /**
   * C0000074: ダーク・ネクロフィア
   * 【自バトルフェイズ開始時】場にこのカード以外の闇属性モンスターがいる場合のみ、
   * 相手モンスター全体と相手プレイヤーに500ダメージを与える。
   */
  C0000074: [
    {
      type: TRIGGER_TYPES.ON_BATTLE_PHASE_START_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自バトルフェイズ開始時: 闇属性がいれば相手全体に500ダメージ',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog } = context;
        const { myField, opponentField, setOpponentField } = getPlayerContext(context);

        // Check if there are other dark attribute monsters on the field
        const hasDarkMonster = myField.some((monster, index) =>
          monster && monster.attribute === '闇' && index !== slotIndex
        );

        if (!hasDarkMonster) {
          addLog('他の闇属性モンスターがいないため発動しない', 'info');
          return;
        }

        // Deal 500 damage to all opponent monsters

        let destroyedCount = 0;

        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              const newHp = monster.currentHp - 500;
              if (newHp <= 0) {
                destroyedCount++;
                addLog(`${monster.name}に500ダメージを与え破壊！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // Deal 500 damage to opponent player
        conditionalDamage(context as any, 500, 'opponent');
        addLog('相手プレイヤーに500ダメージ！', 'damage');
      },
    },
  ],

  /**
   * C0000076: シャドウ・サーバント
   * 【自壊時】デッキからコスト3以下「闇属性」魔法カード1枚を手札に加える。
   */
  C0000076: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: コスト3以下の闇魔法カードをサーチ',
      effect: (context: TriggerContext) => {
        const foundCard = searchCard(context as any, (card) => {
          return card.type === 'magic' &&
                 card.attribute === '闇' &&
                 card.cost <= 3;
        });

        if (!foundCard) {
          context.addLog('デッキに該当カードがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000077: 彷徨える死者
   * 【自壊時】相手モンスター1体に1200ダメージを与える。
   */
  C0000077: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 相手モンスター1体に1200ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        const targetIndex = opponentField.findIndex((m) => m !== null);

        if (targetIndex !== -1) {
          const target = opponentField[targetIndex];
          if (!target) return;
          const newHp = target.currentHp - 1200;

          if (newHp <= 0) {
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setOpponentGraveyard((prev: any) => [...prev, target]);
            addLog(`${target.name}に1200ダメージを与え破壊した！`, 'damage');
          } else {
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = { ...target, currentHp: newHp } as unknown as MonsterInstance;
              return newField;
            });
            addLog(`${target.name}に1200ダメージ！`, 'damage');
          }
        } else {
          addLog('相手の場にモンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000080: 闇魔界の貴婦人
   * 【常時】相手の魔法カードのコストを1増加する。
   * 【自壊時】デッキから《ダーク・ネクロフィア》を手札に加える。
   */
  C0000080: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 相手の魔法カードコスト+1',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // Note: Cost modification system not yet implemented
        // This requires game engine changes to track continuous effects
        addLog('相手の魔法カードのコストを1増加！', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 《ダーク・ネクロフィア》をサーチ',
      effect: (context: TriggerContext) => {
        const foundCard = searchCard(context as any, (card) => {
          return Boolean(card.name && card.name.includes('ダーク・ネクロフィア'));
        });

        if (!foundCard) {
          context.addLog('デッキにダーク・ネクロフィアがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000081: 虚蝕獣・喰らい触手
   * 【自攻撃後】相手モンスターを破壊した場合、破壊したモンスターの攻撃力の50％分のライフを回復する。
   */
  C0000081: [
    {
      type: TRIGGER_TYPES.ON_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃成功時: 破壊モンスターの攻撃力50%回復',
      effect: (context: TriggerContext) => {
        const { destroyedCard } = context;

        if (destroyedCard) {
          const healAmount = Math.floor((destroyedCard.attack || 0) * 0.5);
          if (healAmount > 0) {
            healLife(context as any, healAmount, true);
          }
        }
      },
    },
  ],

  /**
   * C0000082: 怨霊の使者
   * 【自メインフェイズ時】このカードをリリース（墓地に送る）すると、
   * 相手の場にいるモンスター1体の攻撃力を半分にできる。
   */
  C0000082: [
    {
      type: TRIGGER_TYPES.ON_MAIN_PHASE_SELF,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: 'メインフェイズ: リリースして相手モンスター1体の攻撃力半減',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog } = context;
        const { myField, setMyField, setMyGraveyard, opponentField, setOpponentField } = getPlayerContext(context);

        // Find first monster on opponent's field to halve attack
        const targetIndex = opponentField.findIndex((m) => m !== null);

        if (targetIndex === -1) {
          addLog('相手の場にモンスターがいない', 'info');
          return;
        }

        // Release this card (send to graveyard)
        const thisCard = myField[slotIndex ?? -1];
        if (!thisCard) {
          addLog('カードが見つからない', 'info');
          return;
        }

        setMyField((prev: any) => {
          const newField = [...prev];
          newField[slotIndex!] = null;
          return newField;
        });
        setMyGraveyard((prev: any) => [...prev, thisCard]);

        // Halve opponent monster's attack
        const target = opponentField[targetIndex];
        if (!target) return;
        const newAttack = Math.floor((target.attack ?? 0) / 2);

        setOpponentField((prev: any) => {
          const newField = [...prev];
          newField[targetIndex] = {
            ...target,
            attack: newAttack,
            currentAttack: newAttack,
          } as unknown as MonsterInstance;
          return newField;
        });

        addLog(`${thisCard.name}をリリース！${target.name}の攻撃力が半減！(${target.attack ?? 0} → ${newAttack})`, 'info');
      },
    },
  ],

  /**
   * C0000094: 闇鴉の斥候
   * 召喚時、相手のデッキの上から1枚を墓地に送る。
   */
  C0000094: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手デッキ1枚を墓地へ',
      effect: (context: TriggerContext) => {
        millOpponentDeck(context as any, 1);
      },
    },
  ],

  /**
   * C0000095: シャドウ・プランダラー
   * 攻撃時、相手の墓地のカード1枚を除外し、そのコスト＊100分のダメージを相手プレイヤーに与える。
   */
  C0000095: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 相手墓地カード1枚除外、コスト×100ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentGraveyard, setOpponentGraveyard } = getPlayerContext(context);

        if (opponentGraveyard.length > 0) {
          // Banish the first card from opponent's graveyard
          const banishedCard = opponentGraveyard[0];
          setOpponentGraveyard((prev) => prev.slice(1));

          const damage = (banishedCard.cost || 0) * 100;
          addLog(`${banishedCard.name}を除外し、${damage}ダメージ！`, 'damage');

          if (damage > 0) {
            conditionalDamage(context as any, damage, 'opponent');
          }
        } else {
          addLog('相手の墓地にカードがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000096: 奈落の追跡者
   * 召喚時、相手の手札を1枚選び、それがモンスターなら墓地に送る。
   */
  C0000096: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手手札1枚がモンスターなら墓地へ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

        if (opponentHand.length > 0) {
          // Select a random card from opponent's hand
          const randomIndex = Math.floor(Math.random() * opponentHand.length);
          const selectedCard = opponentHand[randomIndex];

          if (selectedCard.type === 'monster') {
            // Send monster to graveyard
            setOpponentHand((prev) => prev.filter((_, index) => index !== randomIndex));
            setOpponentGraveyard((prev) => [...prev, selectedCard]);
            addLog(`相手の手札から${selectedCard.name}を墓地に送った！`, 'info');
          } else {
            addLog(`相手の手札から${selectedCard.name}を選んだがモンスターではない`, 'info');
          }
        } else {
          addLog('相手の手札がない', 'info');
        }
      },
    },
  ],

  /**
   * C0000098: 影喰いの鴉
   * 【自攻撃後】相手モンスターを破壊した場合、相手のデッキの上から2枚を墓地に送る。
   */
  C0000098: [
    {
      type: TRIGGER_TYPES.ON_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃成功時: 相手デッキ2枚をミル',
      effect: (context: TriggerContext) => {
        const { destroyedCard } = context;

        if (destroyedCard) {
          millOpponentDeck(context as any, 2);
        }
      },
    },
  ],

  /**
   * C0000099: 虚蝕獣・爪刃の群れ
   * 召喚時、相手モンスター1体に800ダメージを与える。
   */
  C0000099: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体に800ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        // Find first monster on opponent's field
        const targetIndex = opponentField.findIndex((m) => m !== null);

        if (targetIndex !== -1) {
          const target = opponentField[targetIndex];
          if (!target) return;
          const newHp = target.currentHp - 800;

          if (newHp <= 0) {
            // Destroy monster
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setOpponentGraveyard((prev: any) => [...prev, target]);
            addLog(`${target.name}に800ダメージを与え破壊した！`, 'damage');
          } else {
            // Damage monster
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = { ...target, currentHp: newHp } as unknown as MonsterInstance;
              return newField;
            });
            addLog(`${target.name}に800ダメージ！`, 'damage');
          }
        } else {
          addLog('相手の場にモンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000109: ブラック・オラクル (禁忌カード)
   * 召喚時、場にいる自分以外の全てのモンスターに3000ダメージを与え、
   * この効果で破壊したモンスター1体につき300ダメージを相手プレイヤーに与え、
   * このターン中、相手のモンスター効果と魔法カードの発動を全て無効化。
   * 召喚したターン自分はダイレクトアタックできない。
   */
  C0000109: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 全モンスターに3000、破壊数×300、相手効果無効化、ダイレクト禁止',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog, currentPlayer } = context;
        const { setMyField, setOpponentField } = getPlayerContext(context);

        let destroyedCount = 0;

        // Damage all own monsters except Black Oracle
        setMyField((prev) => {
          return prev.map((monster, index) => {
            if (monster && index !== slotIndex) {
              const newHp = monster.currentHp - 3000;
              if (newHp <= 0) {
                destroyedCount++;
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // Damage all opponent monsters
        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              const newHp = monster.currentHp - 3000;
              if (newHp <= 0) {
                destroyedCount++;
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // Deal damage based on destroyed count
        if (destroyedCount > 0) {
          const damage = destroyedCount * 300;
          addLog(`${destroyedCount}体破壊し、${damage}ダメージ！`, 'damage');
          conditionalDamage(context as any, damage, 'opponent');
        }

        // Note: Effect nullification would require a new game state flag
        // This is a TODO for future implementation
        addLog('このターン、相手の効果と魔法を無効化！', 'info');

        // このターン中、自分はダイレクトアタックできない
        const setMyStatusEffects = currentPlayer === 1
          ? (context as any).setP1StatusEffects
          : (context as any).setP2StatusEffects;
        const myStatusEffects = currentPlayer === 1
          ? (context as any).p1StatusEffects
          : (context as any).p2StatusEffects;

        if (setMyStatusEffects) {
          const result = statusEffectEngine.applyPlayerStatus(
            myStatusEffects || [],
            STATUS_EFFECT_TYPES.NO_DIRECT_ATTACK,
            {
              source: 'C0000109',
              sourceName: 'ブラック・オラクル',
            }
          );
          if (result.success && result.statusEffect) {
            setMyStatusEffects((prev: any) => [...(prev || []), result.statusEffect]);
          }
        }
        addLog('このターン中、ダイレクトアタックできない！', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 自ライフ3000減、闇属性1体特殊召喚',
      effect: (context: TriggerContext) => {
        const { addLog, summoningSickness = false } = context;
        const { myLife, setMyLife, myDeck, setMyDeck, myField, setMyField, currentPlayer } = getPlayerContext(context);

        // Reduce own life by 3000
        const newLife = Math.max(0, myLife - 3000);
        setMyLife(newLife);
        addLog(`ライフが3000減少！(${myLife} → ${newLife})`, 'damage');

        // Special summon a dark attribute monster from deck
        const darkMonster = myDeck.find((card) =>
          card.type === 'monster' && card.attribute === '闇'
        );

        if (darkMonster) {
          const emptySlotIndex = myField.findIndex((slot) => slot === null);

          if (emptySlotIndex !== -1) {
            setMyDeck((prev: any) => prev.filter((c: any) => c.id !== darkMonster.id));
            setMyField((prev: any) => {
              const newField = [...prev];
              newField[emptySlotIndex] = {
                ...darkMonster,
                uniqueId: `${darkMonster.id}_${Date.now()}_${Math.random()}`,
                currentHp: darkMonster.hp ?? 0,
                currentAttack: darkMonster.attack ?? 0,
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                owner: currentPlayer,
                charges: [],
                statusEffects: [],
              } as unknown as MonsterInstance;
              return newField;
            });
            addLog(`${darkMonster.name}を特殊召喚！`, 'info');
          } else {
            addLog('場に空きがないため特殊召喚できない', 'info');
          }
        } else {
          addLog('デッキに闇属性モンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000110: 闇の支配者
   * 召喚時、相手の場にいる禁忌カード以外のコスト3以下モンスター1体のコントロールを奪う。
   */
  C0000110: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手のコスト3以下モンスター1体の支配権奪取',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, setMyField, opponentField, setOpponentField } = getPlayerContext(context);

        // Find first eligible monster (cost 3 or less, not forbidden)
        const targetIndex = opponentField.findIndex((monster) =>
          monster &&
          monster.cost <= 3 &&
          !monster.forbidden
        );

        if (targetIndex !== -1) {
          const stolenMonster = opponentField[targetIndex];
          if (!stolenMonster) return;
          const emptySlotIndex = myField.findIndex((slot) => slot === null);

          if (emptySlotIndex !== -1) {
            // Remove from opponent's field
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });

            // Add to own field
            setMyField((prev: any) => {
              const newField = [...prev];
              newField[emptySlotIndex] = stolenMonster;
              return newField;
            });

            addLog(`${stolenMonster.name}の支配権を奪った！`, 'info');
          } else {
            addLog('自分の場に空きがないため支配権を奪えない', 'info');
          }
        } else {
          addLog('相手の場に対象となるモンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000113: 咆哮の虚蝕獣
   * 召喚時、相手のデッキの上から3枚を墓地に送る。
   */
  C0000113: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手デッキ3枚を墓地へ',
      effect: (context: TriggerContext) => {
        millOpponentDeck(context as any, 3);
      },
    },
  ],

  /**
   * C0000111: 禁忌の守護者
   * 【自壊時】デッキから《ブラック・オラクル》を手札に加える。
   */
  C0000111: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 《ブラック・オラクル》をサーチ',
      effect: (context: TriggerContext) => {
        const foundCard = searchCard(context as any, (card) => {
          return Boolean(card.name && card.name.includes('ブラック・オラクル'));
        });

        if (!foundCard) {
          context.addLog('デッキにブラック・オラクルがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000114: 闇の巨像
   * 【常時】このカードは攻撃できない。
   * 【自壊時】相手フィールド全体に1000ダメージを与える。
   */
  C0000114: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: このカードは攻撃できない',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // Note: Attack restriction system not yet implemented
        // This requires game engine changes
        addLog('このカードは攻撃できない', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 相手フィールド全体に1000ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setOpponentField } = getPlayerContext(context);

        let destroyedCount = 0;

        setOpponentField((prev: any) => {
          return prev.map((monster: any) => {
            if (monster) {
              const newHp = monster.currentHp - 1000;
              if (newHp <= 0) {
                destroyedCount++;
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp } as unknown as MonsterInstance;
            }
            return monster;
          });
        });

        addLog(`相手フィールド全体に1000ダメージ！${destroyedCount}体破壊！`, 'damage');
      },
    },
  ],

  /**
   * C0000115: 禁断の使徒
   * 召喚時、相手モンスター1体に300ダメージを与える。
   */
  C0000115: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体に300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        // Find first monster on opponent's field
        const targetIndex = opponentField.findIndex((m) => m !== null);

        if (targetIndex !== -1) {
          const target = opponentField[targetIndex];
          if (!target) return;
          const newHp = target.currentHp - 300;

          if (newHp <= 0) {
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setOpponentGraveyard((prev: any) => [...prev, target]);
            addLog(`${target.name}に300ダメージを与え破壊した！`, 'damage');
          } else {
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = { ...target, currentHp: newHp } as unknown as MonsterInstance;
              return newField;
            });
            addLog(`${target.name}に300ダメージ！`, 'damage');
          }
        } else {
          addLog('相手の場にモンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000126: ダーク・シャンデリア
   * 召喚時、相手モンスター全ての攻撃力を300下げる。
   */
  C0000126: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手全モンスターの攻撃力-300',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setOpponentField } = getPlayerContext(context);

        let affectedCount = 0;

        setOpponentField((prev: any) => {
          return prev.map((monster: any) => {
            if (monster) {
              affectedCount++;
              const newAttack = Math.max(0, (monster.attack ?? 0) - 300);
              const newCurrentAttack = Math.max(0, ((monster.currentAttack || monster.attack) ?? 0) - 300);
              return {
                ...monster,
                attack: newAttack,
                currentAttack: newCurrentAttack,
              } as unknown as MonsterInstance;
            }
            return monster;
          });
        });

        if (affectedCount > 0) {
          addLog(`相手モンスター${affectedCount}体の攻撃力を300減少！`, 'info');
        } else {
          addLog('相手の場にモンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000231: 幼魔王女リリカ
   * 召喚時、デッキから「ゴシック」または「鎖」魔法カード1枚を手札に加える。
   */
  C0000231: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 「ゴシック」または「鎖」魔法カードをサーチ',
      effect: (context: TriggerContext) => {
        const foundCard = searchCard(context as any, (card) => {
          return Boolean(card.type === 'magic' &&
                 card.name &&
                 (card.name.includes('ゴシック') || card.name.includes('鎖')));
        });

        if (!foundCard) {
          context.addLog('デッキに該当カードがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000232: 奉仕のリリカ
   * 召喚時、自分のSPトークンを１つアクティブにする。
   */
  C0000232: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: SPトークン1つをアクティブ化',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myRestedSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

        if (myRestedSP > 0) {
          setMyActiveSP((prev) => prev + 1);
          setMyRestedSP((prev) => prev - 1);
          addLog('SPトークン1つをアクティブ化！', 'info');
        } else {
          addLog('アクティブ化するSPトークンがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000275: 虚蝕に染まる騎士
   * 召喚時、自分のデッキ上1枚を墓地に送り、それが「未来」属性なら
   * 自分の「闇」モンスター1体の攻撃力を800アップ、違えば自分ライフに800ダメージ。
   */
  C0000275: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: デッキ1枚を墓地へ、未来なら闇+800、違えば自800ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myDeck, setMyDeck, setMyGraveyard, myField, setMyField } = getPlayerContext(context);

        if (myDeck.length > 0) {
          const milledCard = myDeck[0];
          setMyDeck((prev) => prev.slice(1));
          setMyGraveyard((prev) => [...prev, milledCard]);
          addLog(`${milledCard.name}を墓地に送った`, 'info');

          if (milledCard.attribute === '未来') {
            // Buff dark monster
            const darkMonsterIndex = myField.findIndex((monster) =>
              monster && monster.attribute === '闇'
            );

            if (darkMonsterIndex !== -1) {
              const targetMonster = myField[darkMonsterIndex];
              setMyField((prev: any) => {
                const newField = [...prev];
                const monster = newField[darkMonsterIndex];
                if (!monster) return newField;
                newField[darkMonsterIndex] = {
                  ...monster,
                  attack: (monster.attack ?? 0) + 800,
                  currentAttack: ((monster.currentAttack || monster.attack) ?? 0) + 800,
                } as unknown as MonsterInstance;
                return newField;
              });
              addLog(`${targetMonster?.name}の攻撃力が800上昇！`, 'info');
            } else {
              addLog('場に闇属性モンスターがいない', 'info');
            }
          } else {
            // Self damage
            conditionalDamage(context as any, 800, 'self');
          }
        } else {
          addLog('デッキにカードがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000281: 毒使いカムラ
   * 召喚時、自分のモンスター1体の状態異常を回復。
   */
  C0000281: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 自モンスター1体の状態異常回復',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget, monsterIndex } = context;
        const { myField, setMyField } = getPlayerContext(context);

        // 状態異常を持つ自分のモンスターを検索（自分自身は除外）
        const validTargets = myField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster, index }) =>
            monster !== null &&
            index !== monsterIndex &&
            monster.statusEffects &&
            monster.statusEffects.length > 0
          );

        if (validTargets.length === 0) {
          addLog('毒使いカムラの効果: 状態異常を持つ他のモンスターがいません', 'info');
          return;
        }

        // 対象選択
        if (validTargets.length === 1) {
          // 1体のみなら自動選択
          const target = validTargets[0];
          if (!target.monster) return;
          const clearedEffects = target.monster.statusEffects?.map(e => e.type).join(', ') || '';
          setMyField((prev: any) => prev.map((m: any, idx: number) => {
            if (idx === target.index && m) {
              return { ...m, statusEffects: [] };
            }
            return m;
          }));
          addLog(`毒使いカムラの効果: ${target.monster.name}の状態異常を回復！（${clearedEffects}）`, 'heal');
        } else if (setPendingMonsterTarget) {
          // 複数いる場合は選択UI
          setPendingMonsterTarget({
            message: '状態異常を回復する自分のモンスターを選択',
            targetPlayer: 'self',
            validTargets: validTargets.map(t => t.index),
            callback: (selectedIndex: number) => {
              const target = myField[selectedIndex];
              if (!target) return;
              const clearedEffects = (target.statusEffects || []).map(e => e.type).join(', ');
              setMyField((prev: any) => prev.map((m: any, idx: number) => {
                if (idx === selectedIndex && m) {
                  return { ...m, statusEffects: [] };
                }
                return m;
              }));
              addLog(`毒使いカムラの効果: ${target.name}の状態異常を回復！（${clearedEffects}）`, 'heal');
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          // フォールバック: 最初の対象を回復
          const target = validTargets[0];
          if (!target.monster) return;
          const clearedEffects = target.monster.statusEffects?.map(e => e.type).join(', ') || '';
          setMyField((prev: any) => prev.map((m: any, idx: number) => {
            if (idx === target.index && m) {
              return { ...m, statusEffects: [] };
            }
            return m;
          }));
          addLog(`毒使いカムラの効果: ${target.monster.name}の状態異常を回復！（${clearedEffects}）`, 'heal');
        }
      },
    },
  ],

  /**
   * C0000282: 暗殺者の亡魂
   * 【自壊時】相手に600ダメージを与える。
   */
  C0000282: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 相手に600ダメージ',
      effect: (context: TriggerContext) => {
        conditionalDamage(context as any, 600, 'opponent');
      },
    },
  ],

  /**
   * C0000308: シャドウ・クロウ (変幻身)
   * 【変幻身】メインフェイズにSPコストを2払うことで場のこのモンスターを別の形態に変化する。
   */
  C0000308: [
    {
      type: TRIGGER_TYPES.ON_MAIN_PHASE_SELF,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: 'メインフェイズ: SP2消費で別形態に変化',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myActiveSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

        if (myActiveSP >= 2) {
          // Pay SP cost
          setMyActiveSP((prev) => prev - 2);
          setMyRestedSP((prev) => prev + 2);

          // Note: Transformation system not yet implemented
          // This would require searching deck for the transformed form
          addLog('SP2を消費して変化形態へ！', 'info');
          addLog('(変化システム未実装)', 'info');
        } else {
          addLog('SPが足りない（2必要）', 'info');
        }
      },
    },
  ],

  /**
   * C0000315: シャドウ・ネクロレイヴン
   * 攻撃時、相手モンスター1体に400ダメージを与える。
   */
  C0000315: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 相手モンスター1体に400ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        // Find first monster on opponent's field
        const targetIndex = opponentField.findIndex((m) => m !== null);

        if (targetIndex !== -1) {
          const target = opponentField[targetIndex];
          if (!target) return;
          const newHp = target.currentHp - 400;

          if (newHp <= 0) {
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setOpponentGraveyard((prev: any) => [...prev, target]);
            addLog(`${target.name}に400ダメージを与え破壊した！`, 'damage');
          } else {
            setOpponentField((prev: any) => {
              const newField = [...prev];
              newField[targetIndex] = { ...target, currentHp: newHp } as unknown as MonsterInstance;
              return newField;
            });
            addLog(`${target.name}に400ダメージ！`, 'damage');
          }
        } else {
          addLog('相手の場にモンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000364: ヴォランティス・エクリプス (壮麗)
   * 召喚時、自分の墓地のカードを3枚まで除外し、除外した枚数×400ダメージを相手プレイヤーに与える。
   */
  C0000364: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地3枚まで除外、除外数×400ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard } = getPlayerContext(context);

        const banishCount = Math.min(3, myGraveyard.length);

        if (banishCount > 0) {
          // Banish cards from graveyard
          setMyGraveyard((prev) => prev.slice(banishCount));

          const damage = banishCount * 400;
          addLog(`墓地から${banishCount}枚除外し、${damage}ダメージ！`, 'damage');
          conditionalDamage(context as any, damage, 'opponent');
        } else {
          addLog('墓地にカードがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000370: 影羽の鳥民・ノクティス
   * 【常時】自分の《ヴォランティス》モンスターが相手モンスターを戦闘で破壊するたび、
   * そのモンスターの攻撃力の半分を相手プレイヤーにダメージとして与える。
   */
  C0000370: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: ヴォランティス破壊時、攻撃力半分ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // Note: Triggered effect from another card's action
        // This requires game engine changes to detect Volantis destroying monsters
        addLog('ヴォランティスモンスターが破壊する度、追加ダメージ！', 'info');
      },
    },
  ],

  /**
   * C0000384: 魔女エリザヴェット・ヴェイル
   * 【常時】自分の《黒呪》魔法カードのコストを1軽減（重複不可） → continuousEffectsで実装済み
   * 【破壊時】デッキから《呪縛の塔・ヴェルナクール》を1枚場に出す（コスト不要）
   */
  C0000384: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 《呪縛の塔・ヴェルナクール》をデッキから場に出す',
      effect: (context: TriggerContext) => {
        const { addLog, registerCardTriggers, continuousEffectEngine } = context;
        const { myDeck, setMyDeck, setMyFieldCard, setMyGraveyard, myFieldCard, currentPlayer } = getPlayerContext(context);

        // デッキから《呪縛の塔・ヴェルナクール》(C0000386)を検索
        const cardIndex = myDeck.findIndex((card) => card.id === 'C0000386');

        if (cardIndex === -1) {
          addLog('デッキに《呪縛の塔・ヴェルナクール》がない', 'info');
          return;
        }

        // 既存のフィールドカードがある場合は墓地に送る
        if (myFieldCard) {
          // 既存カードのトリガーと常時効果を解除
          if ((myFieldCard as any).uniqueId) {
            unregisterCardTriggers((myFieldCard as any).uniqueId);
            if (continuousEffectEngine) {
              continuousEffectEngine.unregister((myFieldCard as any).uniqueId);
            }
          }
          // 墓地に送る
          setMyGraveyard((prev: any) => [...prev, myFieldCard]);
          addLog(`${myFieldCard.name}が墓地に送られた`, 'info');
        }

        const fieldCard = { ...myDeck[cardIndex], owner: currentPlayer, uniqueId: `${myDeck[cardIndex].id}_${Date.now()}_${Math.random()}` };

        // デッキから除去
        setMyDeck((prev) => {
          const newDeck = [...prev];
          newDeck.splice(cardIndex, 1);
          return newDeck;
        });

        // フィールドカードとして配置
        setMyFieldCard(fieldCard);

        // トリガーを登録
        if (registerCardTriggers) {
          registerCardTriggers(fieldCard, currentPlayer, null);
        }

        // 常時効果を登録
        if (continuousEffectEngine) {
          continuousEffectEngine.register(fieldCard, currentPlayer);
        }

        addLog(`魔女エリザヴェット・ヴェイルの効果で《呪縛の塔・ヴェルナクール》を場に出した！`, 'info');
      },
    },
  ],

  /**
   * C0000385: 呪魂の従者
   * 召喚時、デッキから「エリザヴェット・ヴェイル」1枚を手札に加える。
   */
  C0000385: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 「エリザヴェット・ヴェイル」をサーチ',
      effect: (context: TriggerContext) => {
        const foundCard = searchCard(context as any, (card) => {
          return Boolean(card.name && card.name.includes('エリザヴェット・ヴェイル'));
        });

        if (!foundCard) {
          context.addLog('デッキにエリザヴェット・ヴェイルがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000395: 血呪の棺・ヴェルディクタス (Phase Card)
   * 初期効果: このカード発動時、「ヴァルディスの幻影」トークンを召喚。
   * 1枚重ね: 【自分エンドフェイズ時】自分の《エリザヴェット》モンスター1体に守護（1度だけ受けるダメージ半減）を付与。
   * 2枚重ね: 【自分ターン開始時】墓地の《黒呪》魔法カード1枚を手札に回収。
   * 3枚重ね: デッキから《魔女エリザヴェット・ヴェイル》を場に召喚（コスト不要）。
   */
  C0000395: [
    {
      type: TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '初期効果: ヴァルディスの幻影トークン召喚',
      effect: (context: TriggerContext) => {
        const { addLog, summoningSickness = false } = context;
        const { myField, setMyField, currentPlayer } = getPlayerContext(context);

        const emptySlotIndex = myField.findIndex((slot) => slot === null);

        if (emptySlotIndex !== -1) {
          // Create token
          const token = {
            id: 'TOKEN_VALDIS',
            name: 'ヴァルディスの幻影',
            attribute: '光',
            cost: 0,
            type: 'monster',
            attack: 1000,
            hp: 1000,
            currentHp: 1000,
            currentAttack: 1000,
            category: '【トークン】',
            effect: 'トークン。次のターン開始時に墓地に送られる。',
            uniqueId: `TOKEN_VALDIS_${Date.now()}_${Math.random()}`,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            isToken: true,
            owner: currentPlayer,
            charges: [],
            statusEffects: [],
          } as unknown as MonsterInstance;

          setMyField((prev: any) => {
            const newField = [...prev];
            newField[emptySlotIndex] = token;
            return newField;
          });

          addLog('血呪の棺・ヴェルディクタス【初期効果】: ヴァルディスの幻影トークンを召喚！', 'info');
        } else {
          addLog('血呪の棺・ヴェルディクタス: 場に空きがないためトークンを召喚できない', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚重ね: 《エリザヴェット》モンスターに守護付与',
      condition: (context: TriggerContext) => {
        const { myPhaseCard } = getPlayerContext(context);
        return myPhaseCard && myPhaseCard.id === 'C0000395' && (myPhaseCard.stage || 0) >= 1;
      },
      effect: (context: TriggerContext) => {
        const { addLog, phaseCard } = context;
        const { myPhaseCard, myField, setMyField } = getPlayerContext(context);

        const card = phaseCard || myPhaseCard;
        if (!card || card.id !== 'C0000395' || (card.stage || 0) < 1) return;

        // 《エリザヴェット》モンスターを検索
        const elizavetteIndex = myField.findIndex(m =>
          m && m.name && m.name.includes('エリザヴェット')
        );

        if (elizavetteIndex !== -1) {
          const elizavette = myField[elizavetteIndex];
          setMyField((prev: any) => {
            const newField = [...prev];
            const target = newField[elizavetteIndex];
            if (!target) return newField;

            // 守護状態を付与
            const newStatusEffects = [...(target.statusEffects || [])];
            newStatusEffects.push({
              type: STATUS_EFFECT_TYPES.GUARD,
              duration: -1, // 使用されるまで永続
              source: 'C0000395',
              sourceName: '血呪の棺・ヴェルディクタス',
            });

            newField[elizavetteIndex] = {
              ...target,
              statusEffects: newStatusEffects,
            } as unknown as MonsterInstance;
            return newField;
          });
          addLog(`血呪の棺・ヴェルディクタス【1枚重ね】: ${elizavette?.name}に守護を付与！`, 'info');
        } else {
          addLog('血呪の棺・ヴェルディクタス【1枚重ね】: 場に《エリザヴェット》モンスターがいません', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_TURN_START_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '2枚重ね: 墓地の《黒呪》魔法カードを手札に回収',
      condition: (context: TriggerContext) => {
        const { myPhaseCard } = getPlayerContext(context);
        return myPhaseCard && myPhaseCard.id === 'C0000395' && (myPhaseCard.stage || 0) >= 2;
      },
      effect: (context: TriggerContext) => {
        const { addLog, phaseCard } = context;
        const { myPhaseCard, myGraveyard, setMyGraveyard, setMyHand } = getPlayerContext(context);

        const card = phaseCard || myPhaseCard;
        if (!card || card.id !== 'C0000395' || (card.stage || 0) < 2) return;

        // 墓地の《黒呪》魔法カードを検索
        const blackCurseMagic = myGraveyard.find(c =>
          c.type === 'magic' && c.name && c.name.includes('黒呪')
        );

        if (blackCurseMagic) {
          setMyGraveyard(prev => prev.filter(c => c.uniqueId !== blackCurseMagic.uniqueId));
          setMyHand(prev => [...prev, blackCurseMagic]);
          addLog(`血呪の棺・ヴェルディクタス【2枚重ね】: 墓地から「${blackCurseMagic.name}」を手札に回収！`, 'info');
        } else {
          addLog('血呪の棺・ヴェルディクタス【2枚重ね】: 墓地に《黒呪》魔法カードがありません', 'info');
        }
      },
    },
  ],

  /**
   * C0000407: 魂喰いのレイスヴィット
   * 【相手モンスター攻撃時】そのモンスターに200ダメージ。
   */
  C0000407: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手モンスター攻撃時: 攻撃モンスターに200ダメージ',
      effect: (context: TriggerContext) => {
        const { attackerIndex, addLog } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        // Get the attacking monster
        const attacker = opponentField[attackerIndex ?? -1];
        if (!attacker) {
          addLog('攻撃モンスターが見つからない', 'info');
          return;
        }

        const newHp = attacker.currentHp - 200;

        if (newHp <= 0) {
          setOpponentField((prev: any) => {
            const newField = [...prev];
            newField[attackerIndex!] = null;
            return newField;
          });
          setOpponentGraveyard((prev: any) => [...prev, attacker]);
          addLog(`${attacker.name}に200ダメージを与え破壊！`, 'damage');
        } else {
          setOpponentField((prev: any) => {
            const newField = [...prev];
            newField[attackerIndex!] = { ...attacker, currentHp: newHp } as unknown as MonsterInstance;
            return newField;
          });
          addLog(`${attacker.name}に200ダメージ！`, 'damage');
        }
      },
    },
  ],

  /**
   * C0000409: 血涙の叫女バンシーディス
   * 【死触】このモンスターが与えるダメージが1点でもあれば、その相手モンスターを破壊する。
   * 実装済み: magic-spirit.jsx の executeAttack() 内で shouldApplyShishoku() を使用
   */
  // C0000409: 【死触】は戦闘処理で直接実装されているため、トリガー定義不要

  /**
   * C0000418: 深層の隠蟲クリプトノムス
   * 【被攻撃時】そのターン終了時までこのカードのHPを800アップ。（ターンに1度）
   */
  C0000418: [
    {
      type: TRIGGER_TYPES.ON_ATTACKED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '被攻撃時: HP+800（ターン終了まで）',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog } = context;
        const { myField, setMyField } = getPlayerContext(context);

        if (slotIndex !== undefined && myField[slotIndex]) {
          setMyField((prev: any) => {
            const newField = [...prev];
            const monster = newField[slotIndex];
            if (!monster) return newField;
            newField[slotIndex] = {
              ...monster,
              currentHp: monster.currentHp + 800,
              hp: (monster.hp ?? 0) + 800, // Temporary HP boost
            } as unknown as MonsterInstance;
            return newField;
          });
          addLog('HPが800上昇！（ターン終了まで）', 'info');
          // Note: HP should revert at turn end - requires game engine support
        }
      },
    },
  ],

  /**
   * C0000420: 瘴気の蟻アラクノファグス
   * 【自攻撃時】相手モンスターを破壊した時、相手プレイヤーに300ダメージを与える。
   */
  C0000420: [
    {
      type: TRIGGER_TYPES.ON_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃成功時: 相手に300ダメージ',
      effect: (context: TriggerContext) => {
        const { destroyedCard } = context;

        if (destroyedCard) {
          conditionalDamage(context as any, 300, 'opponent');
        }
      },
    },
  ],

  /**
   * C0000424: 呪灰の翼ダスクドラゴン
   * 【自分エンドフェイズ時】相手フィールド全体に400ダメージを与える。
   */
  C0000424: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: 相手フィールド全体に400ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setOpponentField } = getPlayerContext(context);

        let destroyedCount = 0;

        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              const newHp = monster.currentHp - 400;
              if (newHp <= 0) {
                destroyedCount++;
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        addLog(`相手フィールド全体に400ダメージ！`, 'damage');
        if (destroyedCount > 0) {
          addLog(`${destroyedCount}体破壊！`, 'damage');
        }
      },
    },
  ],

  /**
   * C0000425: 灰骨竜フレアスケルス
   * 【自攻撃時】相手モンスターに与えるダメージを400アップ。
   */
  C0000425: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: ダメージ+400',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('灰骨竜フレアスケルス: 攻撃ダメージ+400！', 'info');
        // accumulatedDamageBonus を使用（React state更新の非同期問題を回避）
        context.accumulatedDamageBonus = (context.accumulatedDamageBonus || 0) + 400;
      },
    },
  ],

  /**
   * C0000426: 灰塵の怨念スペクトラグス
   * 【死触】このモンスターが与えるダメージが1点でもあれば、相手モンスターを破壊する。
   * 実装済み: magic-spirit.jsx の executeAttack() 内で shouldApplyShishoku() を使用
   */
  // C0000426: 【死触】は戦闘処理で直接実装されているため、トリガー定義不要

  /**
   * C0000427: 灰塵の骨霊ファントマリス
   * 【召喚時】自分の墓地の闇属性カード1枚をデッキに戻す。
   */
  C0000427: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地の闇属性カード1枚をデッキに戻す',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyDeck } = getPlayerContext(context);

        const darkCard = myGraveyard.find((card) => card.attribute === '闇');

        if (darkCard) {
          setMyGraveyard((prev) => prev.filter((c) => c.id !== darkCard.id));
          setMyDeck((prev) => [...prev, darkCard]);
          addLog(`${darkCard.name}をデッキに戻した！`, 'info');
        } else {
          addLog('墓地に闇属性カードがない', 'info');
        }
      },
    },
  ],

  // ========================================
  // Field Cards (フィールドカード)
  // ========================================

  /**
   * C0000089: 闇の宮殿
   * 【常時】闇属性モンスターの攻撃力を400アップ。
   * 【相手エンドフェイズ時】自分の場に闇属性モンスターがいるとき、相手の手札を1枚ランダムに墓地に捨てる。
   */
  C0000089: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 闇属性モンスター攻撃力+400',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // Note: Continuous stat modification requires game engine changes
        addLog('闇属性モンスターの攻撃力が400上昇！', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_OPPONENT_END_PHASE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手エンドフェイズ時: 闇属性いれば相手手札1枚破棄',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

        const hasDarkMonster = myField.some((m) => m && m.attribute === '闇');

        if (hasDarkMonster) {
          if (opponentHand.length > 0) {
            const randomIndex = Math.floor(Math.random() * opponentHand.length);
            const discardedCard = opponentHand[randomIndex];

            setOpponentHand((prev) => prev.filter((_, index) => index !== randomIndex));
            setOpponentGraveyard((prev) => [...prev, discardedCard]);
            addLog(`相手の手札から${discardedCard.name}を墓地に捨てた！`, 'info');
          } else {
            addLog('相手の手札がない', 'info');
          }
        }
      },
    },
  ],

  /**
   * C0000108: 虚蝕の呪詛 (Phase Card)
   * 初期効果: 【自分エンドフェイズ時】相手プレイヤーに300ダメージを与える。
   * 1枚重ね: 【自分エンドフェイズ時】自分の闇属性モンスターが場にいる場合、相手のデッキの上から3枚を墓地に送る。
   * 2枚重ね: 【相手がカードをドローしたとき】自分のレスト状態のSPトークン1つをアクティブにする。
   * 3枚重ね: 相手モンスター1体のATK=0（次ターン終了時まで）、自分の闇属性ATK+800、次ターン開始時自分に1000ダメージ。
   */
  C0000108: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '初期効果/1枚重ね: エンドフェイズ時の効果',
      effect: (context: TriggerContext) => {
        const { addLog, phaseCard } = context;
        const { myPhaseCard, myField } = getPlayerContext(context);

        const card = phaseCard || myPhaseCard;
        if (!card || card.id !== 'C0000108') return;

        const stage = card.stage || 0;

        // 初期効果: 相手に300ダメージ
        conditionalDamage(context as any, 300, 'opponent');
        addLog('虚蝕の呪詛【初期効果】: 相手に300ダメージ！', 'damage');

        // 1枚重ね効果（stage >= 1）: 闇属性がいればデッキ3枚墓地送り
        if (stage >= 1) {
          const hasDarkMonster = myField.some((m) => m && m.attribute === '闇');
          if (hasDarkMonster) {
            millOpponentDeck(context as any, 3);
            addLog('虚蝕の呪詛【1枚重ね】: 闇属性モンスターの力で相手デッキ3枚を墓地に！', 'info');
          }
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_OPPONENT_DRAW,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '2枚重ね: 相手ドロー時SPアクティブ化',
      condition: (context: TriggerContext) => {
        const { myPhaseCard } = getPlayerContext(context);
        return myPhaseCard && myPhaseCard.id === 'C0000108' && (myPhaseCard.stage || 0) >= 2;
      },
      effect: (context: TriggerContext) => {
        const { addLog, phaseCard } = context;
        const { myPhaseCard, myRestedSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

        const card = phaseCard || myPhaseCard;
        if (!card || card.id !== 'C0000108' || (card.stage || 0) < 2) return;

        if (myRestedSP > 0) {
          setMyActiveSP(prev => prev + 1);
          setMyRestedSP(prev => prev - 1);
          addLog('虚蝕の呪詛【2枚重ね】: 相手がドローしたことでSPトークン1つをアクティブ化！', 'info');
        } else {
          addLog('虚蝕の呪詛【2枚重ね】: レスト状態のSPトークンがありません', 'info');
        }
      },
    },
  ],

  /**
   * C0000123: 禁忌の王座
   * 【常時】自分のライフが2000以下の場合、闇属性モンスターの召喚コストを1軽減。
   * 【自分エンドフェイズ時】場にいる闇属性モンスター1体につき、相手プレイヤーに300ダメージ。
   */
  C0000123: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: ライフ2000以下で闇コスト-1',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // Note: Cost reduction requires game engine changes
        addLog('闇属性モンスターの召喚コストを1軽減！', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: 闇属性数×300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        const darkCount = myField.filter((m) => m && m.attribute === '闇').length;

        if (darkCount > 0) {
          const damage = darkCount * 300;
          conditionalDamage(context as any, damage, 'opponent');
          addLog(`闇属性${darkCount}体で${damage}ダメージ！`, 'damage');
        }
      },
    },
  ],

  /**
   * C0000124: シャドウ・エンパイア
   * 【相手モンスター召喚時】その攻撃力を500ダウン、そのモンスターに300ダメージ。
   * 【自壊時】自分のライフを1000減らす。
   */
  C0000124: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手召喚時: 攻撃力-500、300ダメージ',
      effect: (context: TriggerContext) => {
        const { card, addLog } = context;

        if (card) {
          // Note: Modifying summoned monster requires game engine changes
          // This trigger should fire during summon and modify the monster
          addLog(`${card.name}の攻撃力を500減少、300ダメージ！`, 'damage');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 自ライフ-1000',
      effect: (context: TriggerContext) => {
        conditionalDamage(context as any, 1000, 'self');
      },
    },
  ],

  /**
   * C0000240: 魔界の幼魔王城
   * 【常時】《リリカ》と名の付くモンスターの攻撃力を500アップ。
   * 【エンドフェイズ時】自分の墓地の《リリカ》モンスター1体をデッキに戻したら、相手プレイヤーに300ダメージ。
   */
  C0000240: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: リリカ攻撃力+500',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('リリカモンスターの攻撃力が500上昇！', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: 'エンドフェイズ時: 墓地リリカをデッキに戻し300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyDeck } = getPlayerContext(context);

        const lilikaCard = myGraveyard.find((card) =>
          card.type === 'monster' && card.name && card.name.includes('リリカ')
        );

        if (lilikaCard) {
          setMyGraveyard((prev) => prev.filter((c) => c.id !== lilikaCard.id));
          setMyDeck((prev) => [...prev, lilikaCard]);
          conditionalDamage(context as any, 300, 'opponent');
          addLog(`${lilikaCard.name}をデッキに戻し、300ダメージ！`, 'damage');
        } else {
          addLog('墓地にリリカモンスターがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000386: 呪縛の塔・ヴェルナクール
   * 【常時】自分の《黒呪》魔法カードのコストを1軽減（エリザヴェットと重複可）。
   */
  C0000386: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 黒呪魔法カードコスト-1（重複可）',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('黒呪魔法カードのコストを1軽減！（エリザヴェットと重複可）', 'info');
      },
    },
  ],

  /**
   * C0000387: 呪核の杖・ザルヴェリオ (Phase Card)
   * 初期効果: 【発動時】相手プレイヤーに200ダメージ。
   * 1枚重ね: 【自分エンドフェイズ時】デッキから《黒呪》魔法カード1枚を手札に加える。
   * 2枚重ね: 【「黒呪」魔法カード発動時】そのターン中、自分の《エリザヴェット》モンスターの攻撃力を500アップ。
   * 3枚重ね: 《呪骸王エリザルヴェドン》がいればATK+1500、いなければデッキから召喚。
   */
  C0000387: [
    {
      type: TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '発動時: 相手に200ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        conditionalDamage(context as any, 200, 'opponent');
        addLog('呪核の杖・ザルヴェリオ【初期効果】: 相手に200ダメージ！', 'damage');
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚重ね: 《黒呪》魔法カードをサーチ',
      condition: (context: TriggerContext) => {
        const { myPhaseCard } = getPlayerContext(context);
        return myPhaseCard && myPhaseCard.id === 'C0000387' && (myPhaseCard.stage || 0) >= 1;
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        // デッキから《黒呪》魔法カードを検索
        const kuromahou = myDeck.find(card =>
          card.type === 'magic' && card.name && card.name.includes('黒呪')
        );

        if (kuromahou) {
          setMyDeck(prev => prev.filter(c => c.uniqueId !== kuromahou.uniqueId));
          setMyHand(prev => [...prev, kuromahou]);
          addLog(`呪核の杖・ザルヴェリオ【1枚重ね】: 「${kuromahou.name}」を手札に加えた！`, 'info');
        } else {
          addLog('呪核の杖・ザルヴェリオ【1枚重ね】: デッキに《黒呪》魔法カードがありません', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_SELF_MAGIC_ACTIVATED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '2枚重ね: 黒呪発動時エリザヴェットATK+500',
      condition: (context: TriggerContext) => {
        const { myPhaseCard } = getPlayerContext(context);
        // 段階2以上 かつ 発動した魔法が黒呪である場合
        const isStage2Plus = myPhaseCard && myPhaseCard.id === 'C0000387' && (myPhaseCard.stage || 0) >= 2;
        const isKurojuMagic = context.card && context.card.name && context.card.name.includes('黒呪');
        return isStage2Plus && isKurojuMagic;
      },
      effect: (context: TriggerContext) => {
        const { addLog, phaseCard } = context;
        const { myPhaseCard, myField, setMyField } = getPlayerContext(context);

        const card = phaseCard || myPhaseCard;
        if (!card || card.id !== 'C0000387' || (card.stage || 0) < 2) return;

        // 自分の《エリザヴェット》モンスターを検索してATK+500
        let buffedCount = 0;
        setMyField((prev: any) => {
          return prev.map((monster: any) => {
            if (monster && monster.name && monster.name.includes('エリザヴェット')) {
              buffedCount++;
              return {
                ...monster,
                attack: (monster.attack ?? 0) + 500,
                currentAttack: ((monster.currentAttack || monster.attack) ?? 0) + 500,
              } as unknown as MonsterInstance;
            }
            return monster;
          });
        });

        if (buffedCount > 0) {
          addLog(`呪核の杖・ザルヴェリオ【2枚重ね】: 黒呪魔法発動により《エリザヴェット》モンスター${buffedCount}体の攻撃力が500アップ！`, 'info');
        } else {
          addLog('呪核の杖・ザルヴェリオ【2枚重ね】: 場に《エリザヴェット》モンスターがいません', 'info');
        }
      },
    },
  ],

  // ========================================
  // 対空モンスター
  // ========================================

  /**
   * C0000548: 竜殺しの狩人
   * 【相手モンスター破壊時】破壊した相手モンスターが飛行モンスターだった時、カードを1枚ドロー。
   */
  C0000548: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_MONSTER_DESTROYED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '飛行モンスター破壊時: 1枚ドロー',
      effect: (context: TriggerContext) => {
        const { destroyedCard, addLog } = context;
        const { hasHikou } = require('../keywordAbilities');

        // 破壊されたモンスターが飛行を持っているか確認
        if (!destroyedCard) {
          return;
        }

        if (!hasHikou(destroyedCard)) {
          // 飛行モンスターではない場合は発動しない
          return;
        }

        // 1枚ドロー
        const drawnCards = drawCards(context, 1);
        if (drawnCards.length > 0) {
          addLog(`竜殺しの狩人の効果: 飛行モンスター「${destroyedCard.name}」を破壊したため1枚ドロー！`, 'info');
        }
      },
    },
  ],

  // ========================================
  // ガーディアンモンスター
  // ========================================

  /**
   * C0000438: カースドウォール・ガーディアン
   * 【被攻撃時】このカードを攻撃したモンスターの攻撃力を500ダウン。
   */
  C0000438: [
    {
      type: TRIGGER_TYPES.ON_ATTACKED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '被攻撃時: 攻撃モンスターの攻撃力-500',
      effect: (context: TriggerContext) => {
        const { attacker, attackerIndex, addLog } = context;
        const { setOpponentField } = getPlayerContext(context);

        if (!attacker || attackerIndex === undefined) {
          return;
        }

        // 攻撃モンスターの攻撃力を永続-500
        setOpponentField((prev: any) => {
          const newField = [...prev];
          if (newField[attackerIndex]) {
            const monster = newField[attackerIndex];
            const currentAtk = monster.currentAttack !== undefined ? monster.currentAttack : (monster.attack ?? 0);
            newField[attackerIndex] = {
              ...monster,
              currentAttack: Math.max(0, (currentAtk ?? 0) - 500),
            } as unknown as MonsterInstance;
            addLog(`カースドウォール・ガーディアンの呪い: ${monster.name}の攻撃力-500！`, 'damage');
          }
          return newField;
        });
      },
    },
  ],

  /**
   * C0000562: 彷徨わせる影
   * 【召喚時】相手の場にモンスターが2体以上いる場合、そのうち1体を【彷徨い】状態にする。
   */
  C0000562: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手2体以上なら1体を彷徨い状態に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField } = getPlayerContext(context);

        // 相手フィールドのモンスター数をカウント
        const opponentMonsterCount = opponentField.filter((m: MonsterInstance | null) => m !== null).length;

        if (opponentMonsterCount < 2) {
          addLog('相手モンスターが2体未満のため発動しない', 'info');
          return;
        }

        // 相手モンスター1体を選択して【彷徨い】状態にする
        selectAndApplyStatusToOpponent(
          context as any,
          STATUS_EFFECT_TYPES.WANDERING,
          {
            duration: 3, // 3ターン経過で自動解除
          },
          '彷徨わせる影'
        );
      },
    },
  ],

  /**
   * C0000563: 魂抜きの使い魔
   * 【自分ターン終了時】このカードが攻撃していない場合、相手モンスター1体を【彷徨い】状態にする。
   */
  C0000563: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 攻撃していなければ相手1体を彷徨い状態に',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog } = context;
        const { myField, opponentField } = getPlayerContext(context);

        // このカードを取得
        const thisCard = myField[slotIndex as number];
        if (!thisCard) return;

        // このカードが攻撃していない場合（canAttack === true のまま）
        if (!thisCard.canAttack) {
          addLog('魂抜きの使い魔: 攻撃済みのため発動しない', 'info');
          return;
        }

        // 相手フィールドにモンスターがいない場合
        const hasOpponentMonster = opponentField.some((m: MonsterInstance | null) => m !== null);
        if (!hasOpponentMonster) {
          addLog('魂抜きの使い魔: 相手モンスターがいないため発動しない', 'info');
          return;
        }

        // 相手モンスター1体を選択して【彷徨い】状態にする
        selectAndApplyStatusToOpponent(
          context as any,
          STATUS_EFFECT_TYPES.WANDERING,
          {
            duration: 3,
          },
          '魂抜きの使い魔'
        );
      },
    },
  ],

  /**
   * C0000564: 黒霧の呪術師
   * 【召喚時】相手モンスター1体を【彷徨い】状態にする。
   * ※基本技は cardEffects/dark.ts で実装
   */
  C0000564: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手1体を彷徨い状態に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField } = getPlayerContext(context);

        // 相手フィールドにモンスターがいない場合
        const hasOpponentMonster = opponentField.some((m: MonsterInstance | null) => m !== null);
        if (!hasOpponentMonster) {
          addLog('黒霧の呪術師: 相手モンスターがいないため発動しない', 'info');
          return;
        }

        // 相手モンスター1体を選択して【彷徨い】状態にする
        selectAndApplyStatusToOpponent(
          context as any,
          STATUS_EFFECT_TYPES.WANDERING,
          {
            duration: 3,
          },
          '黒霧の呪術師'
        );
      },
    },
  ],

  /**
   * C0000566: 彷徨いの王・ワンダラー
   * 【召喚時】相手モンスター2体を【彷徨い】状態にする。
   * ※基本技は cardEffects/dark.ts で実装
   */
  C0000566: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手2体を彷徨い状態に',
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer, p1Field, p2Field, setP1Field, setP2Field } = context;
        const opponentField = currentPlayer === 1 ? p2Field : p1Field;
        const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;

        if (!opponentField || !setOpponentField) return;

        // 【飛行】を持たない対象可能なモンスターを取得
        const validTargets = opponentField
          .map((m: MonsterInstance | null, idx: number) => ({ m, idx }))
          .filter(({ m }) => {
            if (!m) return false;
            // 【飛行】を持つモンスターは免疫
            if (m.keyword && m.keyword.includes('【飛行】')) return false;
            return true;
          });

        if (validTargets.length === 0) {
          addLog('彷徨いの王・ワンダラー: 対象にできるモンスターがいない', 'info');
          return;
        }

        // 最大2体まで彷徨い状態を付与
        const targetsToApply = validTargets.slice(0, 2);
        let appliedCount = 0;

        setOpponentField((prev: any[]) => {
          const newField = [...prev];
          targetsToApply.forEach(({ idx }) => {
            const monster = newField[idx];
            if (monster) {
              const result = statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.WANDERING, {
                duration: 3,
                source: 'C0000566',
                sourceName: '彷徨いの王・ワンダラー',
              });
              if (result.success) {
                appliedCount++;
                newField[idx] = { ...monster };
              }
            }
          });
          return newField;
        });

        if (appliedCount > 0) {
          addLog(`彷徨いの王・ワンダラー: 相手モンスター${appliedCount}体を【彷徨い】状態にした！`, 'damage');
        }
      },
    },
  ],

  /**
   * C0000567: 魂喰らいの魔女・マリス
   * 【召喚時】相手モンスター全てを【彷徨い】状態にする。
   * 【自分ターン終了時】相手の【彷徨い】状態モンスター1体につき、相手プレイヤーに300ダメージ。
   * ※【常時】効果は continuousEffects で実装
   */
  C0000567: [
    // 召喚時: 相手モンスター全てを彷徨い状態に
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手全員を彷徨い状態に',
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer, p1Field, p2Field, setP1Field, setP2Field } = context;
        const opponentField = currentPlayer === 1 ? p2Field : p1Field;
        const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;

        if (!opponentField || !setOpponentField) return;

        let appliedCount = 0;

        setOpponentField((prev: any[]) => {
          const newField = [...prev];
          newField.forEach((monster, idx) => {
            if (!monster) return;
            // 【飛行】を持つモンスターは免疫
            if (monster.keyword && monster.keyword.includes('【飛行】')) return;

            const result = statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.WANDERING, {
              duration: 3,
              source: 'C0000567',
              sourceName: '魂喰らいの魔女・マリス',
            });
            if (result.success) {
              appliedCount++;
              newField[idx] = { ...monster };
            }
          });
          return newField;
        });

        if (appliedCount > 0) {
          addLog(`魂喰らいの魔女・マリス: 相手モンスター${appliedCount}体を【彷徨い】状態にした！`, 'damage');
        } else {
          addLog('魂喰らいの魔女・マリス: 対象がいない', 'info');
        }
      },
    },
    // 自分ターン終了時: 彷徨い数×300ダメージ
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 彷徨い数×300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer, p1Field, p2Field, p1Life, p2Life, setP1Life, setP2Life } = context;
        const opponentField = currentPlayer === 1 ? p2Field : p1Field;
        const setOpponentLife = currentPlayer === 1 ? setP2Life : setP1Life;
        const opponentLife = currentPlayer === 1 ? p2Life : p1Life;

        if (!opponentField || !setOpponentLife) return;

        // 相手の彷徨い状態モンスターをカウント
        const wanderingCount = opponentField.filter((m: MonsterInstance | null) => {
          if (!m?.statusEffects) return false;
          return (m.statusEffects as Array<{ type: string }>).some(
            s => s.type === STATUS_EFFECT_TYPES.WANDERING
          );
        }).length;

        if (wanderingCount === 0) return;

        const damage = wanderingCount * 300;
        setOpponentLife((prev: number) => Math.max(0, prev - damage));
        addLog(`魂喰らいの魔女・マリス: 彷徨い${wanderingCount}体 → 相手に${damage}ダメージ！`, 'damage');
      },
    },
  ],
};

/**
 * Check if a card has dark attribute trigger implementation
 * @param {string} cardId - Card ID (e.g., 'C0000094')
 * @returns {boolean} - True if card has trigger implementation
 */
export const hasDarkCardTrigger = (cardId: string): boolean => {
  const triggers = darkCardTriggers as Record<string, unknown>;
  return cardId !== undefined && cardId !== null && triggers[cardId] !== undefined;
};

/**
 * Get dark attribute card triggers
 * @param {string} cardId - Card ID (e.g., 'C0000094')
 * @returns {Array|null} - Array of trigger objects or null
 */
export const getDarkCardTriggers = (cardId: string): TriggerDefinition[] | null => {
  const triggers = darkCardTriggers as unknown as Record<string, TriggerDefinition[]>;
  if (!cardId || !triggers[cardId]) {
    return null;
  }
  return triggers[cardId];
};

/**
 * Get count of implemented dark attribute cards
 * @returns {number} - Count of implemented cards
 */
export const getDarkCardTriggerCount = () => {
  return Object.keys(darkCardTriggers).length;
};
